// ── design tokens for the Shona TTS pages ────────────────────────────────────
// Single source of truth for colour and heading styles. Every page under
// /shona-tts used to hand-copy its own `colors` object (8 copies); import from
// here instead so a palette change is one edit, not eight.
//
// Token NAMES are deliberately unchanged from those copies, so adopting this
// module was a mechanical swap with no visual diff beyond the contrast fixes
// called out below.

// ── shipping palette: Verdite & Granite ──────────────────────────────────────
// Two accent families, split by role. The line between them is "can you press
// it?", not "is it important?":
//
//   `accent` and its tints — the interactive colour: the Dzose wordmark plus
//   every control (buttons, voice pills, toggles, both sliders, focus rings).
//   Deep verdite #1B4D3E in light mode, flame lily #E2795C in dark.
//
//   `emphasis` — the strongest neutral (near-black on light, white on dark).
//   Copy that is not a control: page headings, eyebrow labels, status lines
//   ("Model ready!"), confirmation text. These read as type, so they take the
//   neutral extreme instead of competing with the controls for attention.
//
// Two secondary marks sit outside both families. `wave` fills the play-bar
// rail; `marker` (msasa copper #B5603A) highlights the word being spoken.
// Neither may ever carry type: #B5603A has a relative luminance of 0.185,
// mid-scale, so it clears 4.5:1 against neither surface here — 4.14:1 on the
// light one, 4.29:1 on a near-black. As non-text marks they owe only the 3:1
// of 1.4.11, which both clear on both surfaces. Promote either to `accent` and
// it has to meet the text floors instead, which it cannot.
//
// ── light and dark are peers, not a base plus an override ────────────────────
// Both palettes below carry the full token set and both are measured against
// CONTRAST_FLOORS. Dark is not a filter over light — it does not even share the
// accent hue: light is verdite green (#1B4D3E), dark is flame lily (#E2795C),
// and the label on the fill flips from white to near-black between them.
// `marker` is the only token that survives the crossing unchanged.
//
// Nothing outside this file holds a colour value. `colors` below exports
// var(--sn-*) references, so every inline style in every page resolves against
// whichever palette useThemeMode has written onto :root — that is what makes
// the toggle instant and free of per-component rewiring.
//
// One exception: the boot screen in public/index.html paints before any JS
// module loads, so it hard-codes `bg` and `textDim` from both palettes. Change
// those four values there too.
export const PALETTES = {
  light: {
    bg: '#F7F6F3',
    bgAlt: '#F1EFEA',
    card: '#FFFFFF',
    sunken: '#EFEDE8',
    border: '#DDD9D2',
    borderActive: '#8C8177',
    text: '#1A1D1B',
    textDim: '#5C6660',
    textDimmer: '#68716C',
    accent: '#1B4D3E',
    accentDim: '#2A5F4E',
    accentHover: '#134034',
    accentBg: 'rgba(27,77,62,0.07)',
    accentSoft: 'rgba(27,77,62,0.16)',
    wave: '#2E8B6B',
    marker: '#B5603A',
    track: '#E4E2DC',
    trackEdge: 'rgba(27,77,62,0.16)',
    emphasis: '#0F1211',
    ink: '#1A1D1B',
    onAccent: '#FFFFFF',
    danger: '#9B2C2C',
    scrim: 'rgba(26,29,27,0.45)',
    shadow: '0 1px 2px rgba(26,29,27,0.06), 0 8px 24px rgba(26,29,27,0.08)',
  },
  // Dark is flame lily — the national flower — not lifted verdite. The two
  // modes deliberately do not share a hue.
  //
  // Dark-only, like every candidate considered here. #E2795C reads 6.30:1 on
  // this near-black ground and 2.55:1 on the light one — a bright value needs
  // darkness behind it. Verdite already carries light mode at 8.92:1.
  //
  // `accentDim` and `borderActive` are deliberately NOT pushed to the edge of
  // 1.4.11. Darkening either until it just held 3:1 would park it at ~3.03,
  // passing with no margin; 20% and 16% leave them at 3.88 and 4.22 against the
  // card while still reading clearly quieter than the accent itself.
  //
  // Known tension: flame lily and the msasa copper `marker` are 6° apart in
  // hue (13 vs 19) and only 1.52:1 apart in luminance, so the spoken-word
  // highlight has little to distinguish it from the controls around it. They
  // rarely share a sightline — the marker sits inside reader text, the accent
  // on chrome and buttons — but if the highlight ever reads as "another
  // button", `marker` is the token to move, not `accent`.
  dark: {
    bg: '#101413',
    bgAlt: '#141917',
    card: '#181D1B',
    sunken: '#0B0F0E',
    border: '#262D2A',
    borderActive: '#BE664D',
    text: '#EDF0EE',
    textDim: '#9AA5A0',
    textDimmer: '#7F8C86',
    accent: '#E2795C',
    accentDim: '#B5614A',
    accentHover: '#E58970',
    accentBg: 'rgba(226,121,92,0.10)',
    accentSoft: 'rgba(226,121,92,0.28)',
    // Same flame lily as `accent`: the play-bar scrubber is a slider, so it
    // matches the speed slider and the buttons rather than splitting the
    // controls across two hues. Light mode keeps its verdite-green waveform.
    wave: '#E2795C',
    marker: '#B5603A',
    track: '#251E1A',
    trackEdge: 'rgba(226,121,92,0.20)',
    emphasis: '#FFFFFF',
    ink: '#0B0F0E',
    onAccent: '#0B0F0E',
    danger: '#F08080',
    scrim: 'rgba(0,0,0,0.72)',
    shadow: '0 1px 2px rgba(0,0,0,0.4), 0 8px 28px rgba(0,0,0,0.5)',
  },
};

export const MODES = ['light', 'dark'];
export const TOKENS = Object.keys(PALETTES.light);

// What every page imports. Each value is a CSS variable reference rather than a
// colour, so `color: colors.accent` compiles to `color: var(--sn-accent)` and
// re-resolves the instant :root changes — no re-render, no context, and
// module-level style objects keep working.
//
// The one place this does not work is an SVG *presentation attribute*
// (`<circle stroke={...}>`); var() is only valid in a CSS property value, so
// those sites use style={{ stroke: colors.accent }} instead.
export const colors = Object.fromEntries(
  TOKENS.map(token => [token, `var(--sn-${token})`])
);

// ── typography ───────────────────────────────────────────────────────────────
// Section headings used to be 15px / uppercase / letterSpacing 1.2 / weight
// 700 — the same treatment as the eyebrow, so several elements competed for
// "most important" and the hierarchy read flat. Headings are now sentence
// case, larger, and normally tracked; uppercase is reserved for `eyebrow`.
export const type = {
  eyebrow: {
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
  },
  sectionHeading: {
    fontSize: 'clamp(19px, 2.6vw, 23px)',
    lineHeight: 1.25,
    letterSpacing: '-0.015em',
    fontWeight: 650,
  },
  // Small labels inside panels (slider legends, field names). Quieter than a
  // section heading and no longer shouting in caps.
  controlLabel: {
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 0.2,
  },
};

// ── contrast maths (WCAG 2.1 relative luminance) ─────────────────────────────
const channel = c => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

// `#rgb`, `#rrggbb` or `rgb()/rgba()` -> [r, g, b, a]. Alpha matters here: half
// the palette is white-on-dark at a fixed opacity, and WCAG ratios are only
// defined for opaque colours, so a translucent token has to be flattened onto
// whatever it actually sits on before it can be measured.
const parse = value => {
  if (typeof value !== 'string') return [0, 0, 0, 1];

  const rgb = value.match(/rgba?\(([^)]+)\)/i);
  if (rgb) {
    const parts = rgb[1].split(/[,/]/).map(p => parseFloat(p.trim()));
    return [parts[0], parts[1], parts[2], parts.length > 3 ? parts[3] : 1];
  }

  let hex = value.replace('#', '');
  if (hex.length === 3) hex = [...hex].map(c => c + c).join('');
  const n = parseInt(hex, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 1];
};

// Source-over composite of `value` onto an already-opaque [r,g,b].
const flatten = (value, base) => {
  const [r, g, b, a] = parse(value);
  if (a >= 1) return [r, g, b];
  return [
    a * r + (1 - a) * base[0],
    a * g + (1 - a) * base[1],
    a * b + (1 - a) * base[2],
  ];
};

const luminance = ([r, g, b]) =>
  0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

// Contrast ratio between two colours, 1–21. Either may be translucent: `b` is
// resolved against the page background first, then `a` is composited onto that
// result — the same order the browser paints them in.
export const contrast = (a, b, pageBg = colors.bg) => {
  const base = flatten(b, parse(pageBg).slice(0, 3));
  const [hi, lo] = [luminance(flatten(a, base)), luminance(base)].sort(
    (x, y) => y - x
  );
  return (hi + 0.05) / (lo + 0.05);
};

// Pairs that must not regress, with the threshold that applies to how each is
// actually used: 7 for AAA body copy, 4.5 for AA text, 3 for non-text UI
// boundaries and focus indicators (1.4.11). Exercised by /style-test.
export const CONTRAST_FLOORS = [
  ['text', 'bg', 7, 'Body copy on page'],
  ['text', 'card', 7, 'Body copy on card'],
  ['textDim', 'bg', 4.5, 'Secondary text on page'],
  ['textDim', 'card', 4.5, 'Secondary text on card'],
  ['textDimmer', 'bg', 4.5, 'Tertiary body text on page'],
  ['emphasis', 'bg', 7, 'Heading / eyebrow on page'],
  ['emphasis', 'card', 7, 'Heading / status line on card'],
  // accentDim is only ever a control outline now, so it takes the 1.4.11 bar
  // rather than the text bar. Its one remaining text use, the eyebrow in the
  // now-playing bar, moved to `emphasis`.
  ['accentDim', 'bg', 3, 'Control outline on page (1.4.11)'],
  ['accentDim', 'card', 3, 'Control outline on card (1.4.11)'],
  ['accent', 'bg', 4.5, 'Wordmark / control label on page'],
  ['accent', 'card', 4.5, 'Control label on card'],
  ['onAccent', 'accent', 4.5, 'Label on primary button'],
  ['onAccent', 'accentHover', 4.5, 'Label on primary button, hover'],
  ['accent', 'accentBg', 4.5, 'Accent text on its own soft wash'],
  ['borderActive', 'card', 3, 'Active/playing card border (1.4.11)'],
  ['borderActive', 'bg', 3, 'Active border on page (1.4.11)'],
  ['accent', 'bg', 3, 'Focus ring on page (1.4.11)'],
  // Non-text marks: 1.4.11's 3:1, never the 4.5 text bar. See the note on
  // `marker` at the top of this file for why that distinction is load-bearing.
  ['wave', 'card', 3, 'Play-bar fill / waveform (1.4.11)'],
  ['marker', 'card', 3, 'Spoken-word marker (1.4.11)'],
  ['danger', 'card', 4.5, 'Error text'],
];

export const auditPalette = palette =>
  CONTRAST_FLOORS.map(([fg, bg, min, label]) => {
    const ratio = contrast(palette[fg], palette[bg], palette.bg);
    return { fg, bg, min, label, ratio, passes: ratio >= min };
  });

// Both shipping modes, so a regression in either is caught. Light and dark are
// audited as peers because they are authored as peers.
export const auditModes = () =>
  Object.fromEntries(MODES.map(m => [m, auditPalette(PALETTES[m])]));

// ── /style-test only ─────────────────────────────────────────────────────────
// Candidate palettes for the preview page, expressed in semantic names so one
// component can render in all of them. `clay-dark` is derived from `colors`
// above so the shipping palette is never described twice.
//
// `accent` here means "the primary interactive colour" — the button fill, the
// focus ring, the thing you press. `wave` and `marker` are the secondary pair:
// `wave` fills the play-bar rail and thumb, `marker` highlights the word being
// spoken. Single-accent palettes just point all three at the same value; a
// two-accent direction like Verdite & Granite is what the split exists for.

const GRANITE_LIGHT = {
  bg: '#F7F5F2',
  surface: '#FFFFFF',
  sunken: '#F0EDE8',
  border: '#DDD7CE',
  borderStrong: '#8C8177',
  text: '#1C1917',
  textDim: '#6B6259',
  eyebrow: '#6B6259',
  accent: '#A63D25',
  accentHover: '#8E3220',
  accentSoft: '#F7E7E1',
  onAccent: '#FFFFFF',
  pill: '#EFEBE4',
  pillText: '#5C544B',
  danger: '#9B2C2C',
  ok: '#3F6B4F',
  focus: '#A63D25',
  wave: '#A63D25',
  marker: '#A63D25',
  shadow: '0 1px 2px rgba(28,25,23,0.06), 0 8px 24px rgba(28,25,23,0.06)',
};

const GRANITE_DARK = {
  bg: '#141210',
  surface: '#1E1B18',
  sunken: '#100E0C',
  border: '#302B26',
  borderStrong: '#736A61',
  text: '#F2EEE8',
  textDim: '#A39A8F',
  eyebrow: '#A39A8F',
  accent: '#E8724E',
  accentHover: '#F08A6A',
  accentSoft: '#3A231B',
  onAccent: '#1A0F0A',
  pill: '#2A251F',
  pillText: '#B5ABA0',
  danger: '#F08080',
  ok: '#79B58C',
  focus: '#E8724E',
  wave: '#E8724E',
  marker: '#E8724E',
  shadow: '0 1px 2px rgba(0,0,0,0.4), 0 8px 28px rgba(0,0,0,0.45)',
};

// The two shipping modes, restated in the preview page's semantic names and
// derived from PALETTES so /style-test can never drift from what ships.
// `accentSoft` is the flat equivalent of `accentBg` composited on the card, so
// the audit has an opaque colour to measure. `eyebrow` takes `emphasis` —
// eyebrow labels are type, so they follow the neutral, not the accent.
const previewOf = (mode, extras) => {
  const p = PALETTES[mode];
  return {
    bg: p.bg,
    surface: p.card,
    sunken: p.sunken,
    border: p.border,
    borderStrong: p.borderActive,
    text: p.text,
    textDim: p.textDim,
    eyebrow: p.emphasis,
    accent: p.accent,
    accentHover: p.accentHover,
    onAccent: p.onAccent,
    pillText: p.textDim,
    danger: p.danger,
    focus: p.accent,
    wave: p.wave,
    marker: p.marker,
    shadow: p.shadow,
    ...extras,
  };
};

// `ok` is not in the shipping set — the app has no success colour of its own —
// so the preview supplies one that clears 4.5:1 as text on each surface.
const VERDITE_LIGHT = previewOf('light', {
  accentSoft: '#EFF3F1',
  pill: '#EFEDE8',
  ok: '#22705A',
});

const VERDITE_DARK = previewOf('dark', {
  accentSoft: '#2C2622',
  pill: '#0E1211',
  // `ok` stays green in both modes — success is semantic, not brand, so it does
  // not follow the accent when the accent turns gold.
  ok: '#5FB394',
});

// The two Verdite entries are what ships; the Granite pair is kept only as the
// comparison the choice was made against.
export const THEMES = {
  'verdite-light': {
    id: 'verdite-light',
    label: 'Verdite light',
    scheme: 'light',
    colors: VERDITE_LIGHT,
  },
  'verdite-dark': {
    id: 'verdite-dark',
    label: 'Flame lily dark',
    scheme: 'dark',
    colors: VERDITE_DARK,
  },
  'granite-light': {
    id: 'granite-light',
    label: 'Granite light',
    scheme: 'light',
    colors: GRANITE_LIGHT,
  },
  'granite-dark': {
    id: 'granite-dark',
    label: 'Granite dark',
    scheme: 'dark',
    colors: GRANITE_DARK,
  },
};

export const THEME_IDS = [
  'verdite-light',
  'verdite-dark',
  'granite-light',
  'granite-dark',
];

export const DEFAULT_THEME_ID = 'verdite-light';

// Prefer the visitor's OS setting on first paint; the switcher overrides it.
export const systemThemeId = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return DEFAULT_THEME_ID;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'verdite-dark'
    : 'verdite-light';
};

// Spread onto a wrapper element's `style` so descendant CSS can read
// var(--sn-accent) etc.
export const cssVars = palette =>
  Object.fromEntries(
    Object.entries(palette).map(([token, value]) => [`--sn-${token}`, value])
  );

// Semantic-name equivalents of CONTRAST_FLOORS, for the preview page's themes.
export const AUDIT_PAIRS = [
  ['text', 'bg', 7, 'Body copy on page'],
  ['text', 'surface', 7, 'Body copy on card'],
  ['textDim', 'bg', 4.5, 'Secondary text on page'],
  ['textDim', 'surface', 4.5, 'Secondary text on card'],
  ['eyebrow', 'bg', 4.5, 'Eyebrow label ("Dzose") on page'],
  ['accent', 'bg', 4.5, 'Accent as text on page'],
  ['accent', 'surface', 4.5, 'Accent as text on card'],
  ['onAccent', 'accent', 4.5, 'Label on primary button'],
  ['onAccent', 'accentHover', 4.5, 'Label on primary button, hover'],
  ['accent', 'accentSoft', 4.5, 'Accent text in soft chip'],
  ['accent', 'sunken', 4.5, 'Accent on textarea well'],
  ['borderStrong', 'surface', 3, 'Input border on card (1.4.11)'],
  ['borderStrong', 'bg', 3, 'Input border on page (1.4.11)'],
  ['focus', 'bg', 3, 'Focus ring on page (1.4.11)'],
  // `wave` and `marker` are non-text marks, so 1.4.11's 3:1 is the bar they
  // owe — not 4.5. A palette that wants either of them to carry type has to
  // promote it to `accent` and meet the text floors above.
  ['wave', 'surface', 3, 'Play-bar fill / waveform (1.4.11)'],
  ['marker', 'surface', 3, 'Spoken-word marker (1.4.11)'],
  ['pillText', 'pill', 4.5, 'Dialect pill text'],
  ['danger', 'surface', 4.5, 'Error text'],
  ['ok', 'surface', 4.5, 'Success / ready text'],
];

export const auditTheme = palette =>
  AUDIT_PAIRS.map(([fg, bg, min, label]) => {
    const ratio = contrast(palette[fg], palette[bg], palette.bg);
    return { fg, bg, min, label, ratio, passes: ratio >= min };
  });
